// Path assumption: placed at Pages/Outreach/Compensation/WorkerCompensationSetupPage.tsx,
// same nesting depth as Pages/Outreach/Templates/TemplateFormPage.tsx — adjust the
// relative import depths below if you place it somewhere else.
import { ChangeEvent, ReactNode } from "react";
import { ViewItem, viewItemProps } from "../../../Components/Views/ViewItem";
import { controllers } from "../../../Controllers/Controllers";
import { SalaryConfigType, WorkerSalaryRowType, CompensationType } from "../../../DataTypes/compensation";
// import '../outreach-styles.css'
// import '../Categories/categories-styles.css'
import './compensation-styles.css'

const todayISO = () => new Date().toISOString().slice(0, 10)

const formatDateShort = (iso: string) =>
    new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

const formatMoney = (value: string | number) => `$${Number(value).toFixed(2)}`

type SalaryDraft = {
    salary_day: string;
    basic_salary_amount: string;
    date_of_employment: string;
    date_of_lay_off: string;
}

const draftFromConfig = (config: SalaryConfigType | null): SalaryDraft => ({
    salary_day: config ? String(config.salary_day) : '1',
    basic_salary_amount: config ? config.basic_salary_amount : '',
    date_of_employment: config ? config.date_of_employment : todayISO(),
    date_of_lay_off: config?.date_of_lay_off || '',
})

type WorkerCompensationSetupPageProps = viewItemProps & {
    // There's no router wiring in this file — the parent decides what
    // happens when the admin wants to issue a compensation for a given
    // worker (e.g. switch to <IssueCompensationPage workerId={id} />,
    // or navigate to a route that renders it).
    onIssueCompensation?: (workerId: number) => void;
}

type WorkerCompensationSetupPageState = {
    workers: WorkerSalaryRowType[];
    loading: boolean;
    error: string | null;
    needsAuth: boolean;

    editingWorkerId: number | null;
    draft: SalaryDraft | null;
    saving: boolean;
    saveError: string | null;

    expandedWorkerId: number | null;
    compensationsByWorker: Record<number, CompensationType[]>;
    compensationsLoadingId: number | null;
}

class WorkerCompensationSetupPage extends ViewItem {
    props: WorkerCompensationSetupPageProps;
    state: WorkerCompensationSetupPageState;

    constructor(props: WorkerCompensationSetupPageProps) {
        super(props)
        this.props = props
        this.state = {
            workers: [],
            loading: true,
            error: null,
            needsAuth: false,

            editingWorkerId: null,
            draft: null,
            saving: false,
            saveError: null,

            expandedWorkerId: null,
            compensationsByWorker: {},
            compensationsLoadingId: null,
        }
    }

    componentDidMount(): void {
        this.loadWorkers()
    }

    loadWorkers = async () => {
        this.setState({ loading: true, error: null, needsAuth: false })
        if (!controllers().auth.userToken) {
            this.setState({ loading: false, needsAuth: true })
            return
        }
        const response = await controllers().compensation.getSalaryConfigs()
        if (!response || response.statusCode !== 200) {
            this.setState({ loading: false, error: response?.error || 'Failed to load workers.' })
            return
        }
        this.setState({ loading: false, workers: response.data?.workers || [] })
    }

    startEdit = (worker: WorkerSalaryRowType) => {
        this.setState({ editingWorkerId: worker.id, draft: draftFromConfig(worker.salary_config), saveError: null })
    }

    cancelEdit = () => {
        this.setState({ editingWorkerId: null, draft: null, saveError: null })
    }

    updateDraftField = (field: keyof SalaryDraft) => (e: ChangeEvent<HTMLInputElement>) => {
        this.setState((prev: WorkerCompensationSetupPageState) => ({
            draft: prev.draft ? { ...prev.draft, [field]: e.target.value } : prev.draft,
        }))
    }

    saveDraft = async () => {
        const { editingWorkerId, draft } = this.state
        if (!editingWorkerId || !draft) return

        if (!draft.basic_salary_amount || Number.isNaN(Number(draft.basic_salary_amount))) {
            this.setState({ saveError: 'Enter a valid basic salary amount.' })
            return
        }
        const salaryDayNum = Number(draft.salary_day)
        if (!salaryDayNum || salaryDayNum < 1 || salaryDayNum > 31) {
            this.setState({ saveError: 'Salary day must be between 1 and 31.' })
            return
        }
        if (!draft.date_of_employment) {
            this.setState({ saveError: 'Date of employment is required.' })
            return
        }

        this.setState({ saving: true, saveError: null })
        const response = await controllers().compensation.setSalaryConfig({
            user_id: editingWorkerId,
            salary_day: salaryDayNum,
            basic_salary_amount: draft.basic_salary_amount,
            date_of_employment: draft.date_of_employment,
            date_of_lay_off: draft.date_of_lay_off || null,
        })
        if (!response || response.statusCode !== 200) {
            this.setState({ saving: false, saveError: response?.error || 'Failed to save.' })
            return
        }
        this.setState((prev: WorkerCompensationSetupPageState) => ({
            saving: false,
            editingWorkerId: null,
            draft: null,
            workers: prev.workers.map(w => w.id === editingWorkerId ? { ...w, salary_config: response.data } : w),
        }))
    }

    toggleExpand = (worker: WorkerSalaryRowType) => {
        if (this.state.expandedWorkerId === worker.id) {
            this.setState({ expandedWorkerId: null })
            return
        }
        this.setState({ expandedWorkerId: worker.id })
        if (!this.state.compensationsByWorker[worker.id]) {
            this.loadWorkerCompensations(worker.id)
        }
    }

    loadWorkerCompensations = async (workerId: number) => {
        this.setState({ compensationsLoadingId: workerId })
        const response = await controllers().compensation.listCompensations({ worker_id: workerId, per_page: 5 })
        this.setState((prev: WorkerCompensationSetupPageState) => ({
            compensationsLoadingId: null,
            compensationsByWorker: {
                ...prev.compensationsByWorker,
                [workerId]: (response && response.statusCode === 200) ? (response.data?.compensations || []) : [],
            },
        }))
    }

    renderEditForm = (worker: WorkerSalaryRowType) => {
        const { draft, saving, saveError } = this.state
        if (!draft) return null
        return (
            <div className="comp-salary-edit-form">
                {saveError && <div className="category-form-error">{saveError}</div>}
                <div className="lead-form-grid">
                    <div className="lead-form-field">
                        <label className="category-form-label">Salary day (1–31)</label>
                        <input
                            type="number" min={1} max={31}
                            className="category-form-input"
                            value={draft.salary_day}
                            onChange={this.updateDraftField('salary_day')}
                        />
                    </div>
                    <div className="lead-form-field">
                        <label className="category-form-label">Basic salary ($)</label>
                        <input
                            type="number" step="0.01" min={0}
                            className="category-form-input"
                            value={draft.basic_salary_amount}
                            onChange={this.updateDraftField('basic_salary_amount')}
                        />
                    </div>
                    <div className="lead-form-field">
                        <label className="category-form-label">Date of employment</label>
                        <input
                            type="date"
                            className="category-form-input"
                            value={draft.date_of_employment}
                            onChange={this.updateDraftField('date_of_employment')}
                        />
                    </div>
                    <div className="lead-form-field">
                        <label className="category-form-label">Date laid off (optional)</label>
                        <input
                            type="date"
                            className="category-form-input"
                            value={draft.date_of_lay_off}
                            onChange={this.updateDraftField('date_of_lay_off')}
                        />
                    </div>
                </div>
                <div className="lead-form-actions comp-salary-edit-actions">
                    <button className="outreach-modal-cancel" onClick={this.cancelEdit} disabled={saving}>Cancel</button>
                    <button className="outreach-add-btn" onClick={this.saveDraft} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        )
    }

    renderCompensationsList = (worker: WorkerSalaryRowType) => {
        const { compensationsByWorker, compensationsLoadingId } = this.state
        const comps = compensationsByWorker[worker.id]

        if (compensationsLoadingId === worker.id) {
            return <div className="outreach-state"><div className="outreach-spinner" /></div>
        }
        if (!comps || comps.length === 0) {
            return <div className="outreach-muted comp-empty-row">No compensations issued yet.</div>
        }
        return (
            <div className="comp-mini-list">
                {comps.map(c => (
                    <div key={c.id} className="comp-mini-row">
                        <div className="comp-period-row-data">
                            <span>{formatDateShort(c.period_start)} – {formatDateShort(c.period_end)}</span>
                            <span className="comp-mini-total">{formatMoney(c.total_amount)}</span>
                        </div>
                        {c.invoice_url && (
                            <div className="comp-links-row">
                                <a className="comp-mini-link" href={`${c.invoice_url}?format=pdf&download=true`} target="_blank" rel="noreferrer">
                                    Download invoice
                                </a>
                                <a className="comp-mini-link" href={c.invoice_url} target="_blank" rel="noreferrer">
                                    View invoice
                                </a>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        )
    }

    renderWorkerRow = (worker: WorkerSalaryRowType) => {
        const { editingWorkerId, expandedWorkerId } = this.state
        const config = worker.salary_config
        const isEditing = editingWorkerId === worker.id
        const isExpanded = expandedWorkerId === worker.id

        return (
            <div key={worker.id} className="comp-worker-card">
                <div className="comp-worker-card-header">
                    <div>
                        <span className="comp-worker-name">{worker.username || worker.email}</span>
                        <div className="comp-worker-email">{worker.email}</div>
                    </div>

                    {config ? (
                        <div className="comp-worker-summary">
                            <div className="comp-summary-stat">
                                <span className="comp-summary-value">{formatMoney(config.basic_salary_amount)}</span>
                                <span className="comp-summary-label">/ month</span>
                            </div>
                            <div className="comp-summary-stat">
                                <span className="comp-summary-value">Day {config.salary_day}</span>
                                <span className="comp-summary-label">pay day</span>
                            </div>
                            <div className="comp-summary-stat">
                                <span className={`comp-summary-value ${config.is_active ? 'comp-status-active' : 'comp-status-inactive'}`}>
                                    {config.is_active ? 'Active' : 'Laid off'}
                                </span>
                                <span className="comp-summary-label">since {formatDateShort(config.date_of_employment)}</span>
                            </div>
                        </div>
                    ) : (
                        <span className="outreach-muted">No salary configured yet</span>
                    )}

                    <div className="comp-worker-actions">
                        <button className="outreach-banner-action" onClick={() => this.startEdit(worker)}>
                            {config ? 'Edit' : 'Set up'}
                        </button>
                        <button
                            className="outreach-add-btn comp-issue-btn"
                            disabled={!config}
                            title={!config ? 'Set up a salary config first' : ''}
                            onClick={() => this.props.onIssueCompensation?.(worker.id)}
                        >
                            Issue compensation
                        </button>
                        <button className="comp-expand-btn" onClick={() => this.toggleExpand(worker)}>
                            {isExpanded ? 'Hide history ▲' : 'History ▼'}
                        </button>
                    </div>
                </div>

                {isEditing && this.renderEditForm(worker)}
                {isExpanded && this.renderCompensationsList(worker)}
            </div>
        )
    }

    render(): ReactNode {
        const { workers, loading, error, needsAuth } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Payroll setup</h1>
                        <p className="outreach-subtitle">Configure each worker's salary and review their compensation history.</p>
                    </div>
                </div>

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to manage payroll.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>Sign In</button>
                    </div>
                )}

                {!loading && error && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{error}</span>
                        <button className="outreach-banner-action" onClick={this.loadWorkers}>Retry</button>
                    </div>
                )}

                {loading && <div className="outreach-state"><div className="outreach-spinner" /></div>}

                {!loading && !error && !needsAuth && workers.length === 0 && (
                    <div className="outreach-state">No workers found.</div>
                )}

                {!loading && !error && !needsAuth && workers.length > 0 && (
                    <div className="comp-worker-list">
                        {workers.map(w => this.renderWorkerRow(w))}
                    </div>
                )}
            </div>
        )
    }
}

export { WorkerCompensationSetupPage }
